import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../utils/api';
import { Card, PageHeader, Btn, Badge, Input, Select, Modal } from '../components/Layout';

const STATUSES = ['new','emailed','called','replied','booked','proposal','placed','closed'];
const STATUS_COLORS = { new: 'var(--muted)', emailed: 'var(--amber)', called: 'var(--green)', replied: 'var(--accent)', booked: 'var(--purple)', proposal: '#ff6b6b', placed: 'var(--green)', closed: 'var(--muted)' };

// ── Lead Scoring ─────────────────────────────────────────────────────────────
const calcScore = (lead) => {
  let score = 0;
  if (lead.status === 'replied') score += 30;
  else if (lead.status === 'booked') score += 50;
  else if (lead.status === 'emailed') score += 10;
  else if (lead.status === 'called') score += 20;
  if (lead.title?.match(/CTO|VP|Director|Founder/i)) score += 20;
  if (lead.lastContactedAt) score += 10;
  if (lead.sequenceId) score += 10;
  return Math.min(score, 100);
};
const scoreLabel = (s) => s >= 70 ? '🔥' : s >= 40 ? '🟡' : '⚪';

// ── Email Modal ──────────────────────────────────────────────────────────────
function EmailModal({ lead, company, onClose }) {
  const [subject, setSubject] = useState('');
  const [body,    setBody]    = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);

  useEffect(() => {
    api.post('/ai/write-email', { lead, type: lead.type, company })
      .then(r => { setSubject(r.data.subject); setBody(r.data.body); })
      .catch(() => { setSubject('Introduction from ' + company); setBody('Hi ' + lead.name + ',\n\nI wanted to reach out...'); })
      .finally(() => setLoading(false));
  }, []);

  const send = async () => {
    setSending(true);
    try {
      await api.post('/emails/send', { to: lead.email, subject, body, leadId: lead._id, leadName: lead.name, company });
      setSent(true);
      setTimeout(onClose, 1200);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send');
    } finally { setSending(false); }
  };

  return (
    <Modal title={`✉️ AI Email — ${lead.name}`} onClose={onClose}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>TO</div>
        <div style={{ color: 'var(--mid)', fontSize: 13 }}>{lead.email}</div>
      </div>
      <Input label="Subject" value={subject} onChange={e => setSubject(e.target.value)} />
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 5 }}>Body</div>
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--accent)', fontSize: 13 }}>⚡ AI writing your email...</div>
        ) : (
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={8} style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 12px', color: 'var(--text)', fontSize: 13, resize: 'vertical', lineHeight: 1.7, fontFamily: 'inherit' }} />
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Btn onClick={onClose} small>Cancel</Btn>
        <Btn primary small onClick={send} disabled={loading || sending || sent}>
          {sent ? '✓ Sent!' : sending ? 'Sending...' : 'Send Email'}
        </Btn>
      </div>
    </Modal>
  );
}

// ── AI Call Modal (real / simulated outbound) ────────────────────────────────
function AICallModal({ lead, company, onClose }) {
  const [phase,     setPhase]     = useState('ready');
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState('');
  const [provider,  setProvider]  = useState('');
  const [copied,    setCopied]    = useState(false);

  const initiateCall = async () => {
    if (!lead.phone) { setError('This lead has no phone number. Add one first.'); return; }
    setPhase('initiating');
    try {
      const r = await api.post('/ai/initiate-call', { lead, type: lead.type, company });
      setProvider(r.data.provider);
      if (r.data.provider === 'vapi') {
        setPhase('ringing');
        setResult(r.data);
      } else {
        setPhase('done');
        setResult(r.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Call failed');
      setPhase('error');
    }
  };

  const copy = text => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <Modal title="🤖 AI Outbound Call" onClose={onClose} width={560}>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 52, marginBottom: 8 }}>
          {phase === 'ready'      && '🤖'}
          {phase === 'initiating' && '⏳'}
          {phase === 'ringing'    && '📞'}
          {phase === 'done'       && (result?.outcome === 'positive' ? '✅' : '📞')}
          {phase === 'error'      && '❌'}
        </div>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{lead.name}</div>
        <div style={{ color: 'var(--muted)', fontSize: 13 }}>{lead.phone || 'No phone number'}</div>
        <div style={{ marginTop: 8, fontSize: 13 }}>
          {phase === 'ready'      && <span style={{ color: 'var(--muted)' }}>AI agent will call this lead on your behalf</span>}
          {phase === 'initiating' && <span style={{ color: 'var(--amber)' }}>⏳ Connecting to AI calling service...</span>}
          {phase === 'ringing'    && <span style={{ color: 'var(--green)' }}>📞 Call initiated via Vapi.ai — check the Calls log for transcript</span>}
          {phase === 'done'       && <span style={{ color: result?.outcome === 'positive' ? 'var(--green)' : 'var(--muted)' }}>✅ Call complete · {result?.duration}s · {provider}</span>}
          {phase === 'error'      && <span style={{ color: 'var(--red)' }}>{error}</span>}
        </div>
      </div>

      {phase === 'ready' && (
        <div style={{ background: 'var(--surface)', borderRadius: 8, padding: 16, marginBottom: 20, fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--accent)' }}>What the AI agent will do:</div>
          {lead.type === 'bdm' ? (
            <ul style={{ color: 'var(--mid)', lineHeight: 2, margin: 0, paddingLeft: 18 }}>
              <li>Introduce {company} as a software dev partner</li>
              <li>Qualify interest in outsourcing/offshoring</li>
              <li>Offer to book a 15-min discovery call</li>
              <li>Log transcript + CRM note automatically</li>
            </ul>
          ) : (
            <ul style={{ color: 'var(--mid)', lineHeight: 2, margin: 0, paddingLeft: 18 }}>
              <li>Ask about current bench availability</li>
              <li>Discuss rate expectations (C2C/W2)</li>
              <li>Confirm work authorisation status</li>
              <li>Log transcript + CRM note automatically</li>
            </ul>
          )}
          {!lead.phone && <div style={{ color: 'var(--red)', marginTop: 8, fontWeight: 600 }}>⚠️ No phone number — add one to this lead first</div>}
        </div>
      )}

      {(phase === 'done') && result?.transcript && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>📋 Call Transcript</div>
          <div style={{ background: 'var(--surface)', borderRadius: 8, padding: 14, maxHeight: 220, overflowY: 'auto', fontSize: 12, lineHeight: 1.8 }}>
            {result.transcript.split('\n').map((line, i) => (
              <div key={i} style={{ color: line.startsWith('Agent') ? 'var(--accent)' : 'var(--text)', marginBottom: 2 }}>{line}</div>
            ))}
          </div>
        </div>
      )}

      {phase === 'done' && result?.summary && (
        <div style={{ background: 'var(--green)11', border: '1px solid var(--green)33', borderRadius: 8, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--green)', fontWeight: 700, marginBottom: 4 }}>📋 CRM NOTE SAVED</div>
          <div style={{ color: 'var(--mid)', fontSize: 13, lineHeight: 1.6 }}>{result.summary}</div>
          <Btn small style={{ marginTop: 8 }} onClick={() => copy(result.summary)}>{copied ? '✓ Copied' : '📋 Copy Note'}</Btn>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        {phase === 'ready' && <Btn primary onClick={initiateCall} disabled={!lead.phone}>📞 Start AI Call</Btn>}
        {phase === 'ringing' && <Btn onClick={onClose}>Close (call running in background)</Btn>}
        {(phase === 'done' || phase === 'error') && <Btn primary onClick={onClose}>Close</Btn>}
      </div>
    </Modal>
  );
}

// ── WhatsApp Modal ────────────────────────────────────────────────────────────
function WhatsAppModal({ lead, company, onClose }) {
  const [message, setMessage] = useState(`Hi ${lead.name.split(' ')[0]} 👋 quick follow-up on my email. Worth a 15-min call? Happy to share more details.`);
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);

  const send = async () => {
    setSending(true);
    try {
      await api.post('/whatsapp/send', { phone: lead.phone, message, leadId: lead._id, leadName: lead.name, company });
      setSent(true);
      setTimeout(onClose, 1500);
    } catch (err) { alert(err.response?.data?.error || 'WhatsApp send failed'); }
    finally { setSending(false); }
  };

  return (
    <Modal title={`💬 WhatsApp — ${lead.name}`} onClose={onClose} width={480}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>TO</div>
        <div style={{ color: 'var(--mid)', fontSize: 13 }}>{lead.phone || '⚠️ No phone number'}</div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 5 }}>Message</div>
        <textarea value={message} onChange={e => setMessage(e.target.value)} rows={5}
          style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 12px', color: 'var(--text)', fontSize: 13, resize: 'vertical', lineHeight: 1.7, fontFamily: 'inherit' }} />
      </div>
      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 16 }}>
        💡 WhatsApp requires WHATSAPP_PHONE_ID + WHATSAPP_TOKEN in .env
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Btn small onClick={onClose}>Cancel</Btn>
        <Btn primary small onClick={send} disabled={sending || sent || !lead.phone}>
          {sent ? '✅ Sent!' : sending ? 'Sending...' : '💬 Send WhatsApp'}
        </Btn>
      </div>
    </Modal>
  );
}

// ── Call Modal ───────────────────────────────────────────────────────────────
function CallModal({ lead, company, onClose }) {
  const [phase,      setPhase]      = useState('dialing');
  const [lines,      setLines]      = useState([]);
  const [summary,    setSummary]    = useState('');
  const [timer,      setTimer]      = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('connected'), 1500);
    const t2 = setTimeout(async () => {
      setPhase('talking');
      const iv = setInterval(() => setTimer(t => t + 1), 1000);
      try {
        const r = await api.post('/ai/simulate-call', { lead, type: lead.type, company });
        const ls = r.data.transcript.split('\n').filter(Boolean);
        ls.forEach((l, i) => setTimeout(() => setLines(prev => [...prev, l]), i * 1200));
        setTimeout(() => {
          clearInterval(iv);
          setSummary(r.data.summary);
          setPhase('summary');
          api.post('/calls/log', { leadId: lead._id, leadName: lead.name, company, outcome: r.data.summary, transcript: r.data.transcript }).catch(() => {});
        }, ls.length * 1200 + 500);
      } catch { clearInterval(iv); setPhase('summary'); setSummary('Call completed. Log manually.'); }
    }, 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const fmt = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <Modal title="📞 AI Call Simulator" onClose={onClose} width={500}>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>📞</div>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{lead.name}</div>
        <div style={{ color: 'var(--muted)', fontSize: 13 }}>{lead.phone || 'Phone not available'}</div>
        <div style={{ marginTop: 8, fontSize: 13 }}>
          {phase === 'dialing'   && <span style={{ color: 'var(--amber)' }}>⏳ Dialing...</span>}
          {phase === 'connected' && <span style={{ color: 'var(--green)' }}>✅ Connected</span>}
          {phase === 'talking'   && <span style={{ color: 'var(--green)' }}>🔴 Live — {fmt(timer)}</span>}
          {phase === 'summary'   && <span style={{ color: 'var(--accent)' }}>✅ Call Complete</span>}
        </div>
      </div>

      {(phase === 'talking' || phase === 'summary') && lines.length > 0 && (
        <div style={{ background: 'var(--surface)', borderRadius: 8, padding: 14, maxHeight: 200, overflowY: 'auto', fontSize: 13, lineHeight: 1.8, marginBottom: 16 }}>
          {lines.map((l, i) => (
            <div key={i} style={{ color: l.startsWith('Agent') ? 'var(--accent)' : 'var(--text)', marginBottom: 2 }}>{l}</div>
          ))}
        </div>
      )}

      {phase === 'summary' && summary && (
        <div style={{ background: 'var(--green)11', border: '1px solid var(--green)33', borderRadius: 8, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--green)', fontWeight: 700, marginBottom: 6 }}>📋 CRM NOTE SAVED</div>
          <div style={{ color: 'var(--mid)', fontSize: 13, lineHeight: 1.6 }}>{summary}</div>
        </div>
      )}

      <div style={{ textAlign: 'center' }}>
        {phase === 'summary'
          ? <Btn primary onClick={onClose}>Close & Save</Btn>
          : <Btn danger onClick={onClose}>End Call</Btn>
        }
      </div>
    </Modal>
  );
}

// ── Add Lead Modal ───────────────────────────────────────────────────────────
function AddLeadModal({ company, onClose, onAdded }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', title: '', firmName: '', industry: '', skill: '', type: 'bdm', status: 'new' });
  const [busy, setBusy] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post('/leads', { ...form, company });
      onAdded(r.data); onClose();
    } catch (err) { alert(err.response?.data?.error || 'Failed to add lead'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="➕ Add Lead" onClose={onClose}>
      <form onSubmit={submit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label="Full Name *" value={form.name}     onChange={set('name')}     required />
          <Input label="Email *"     value={form.email}    onChange={set('email')}    required type="email" />
          <Input label="Phone"       value={form.phone}    onChange={set('phone')}    />
          <Input label="Title"       value={form.title}    onChange={set('title')}    />
          <Input label="Company"     value={form.firmName} onChange={set('firmName')} />
          <Input label="Industry"    value={form.industry} onChange={set('industry')} />
        </div>
        <Select label="Type" value={form.type} onChange={set('type')}>
          <option value="bdm">BDM (Software Projects)</option>
          <option value="recruiting">Recruiting (C2C/W2)</option>
        </Select>
        {form.type === 'recruiting' && <Input label="Skill" value={form.skill} onChange={set('skill')} placeholder="Java, Salesforce, SAP..." />}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <Btn onClick={onClose} small>Cancel</Btn>
          <Btn primary small disabled={busy}>{busy ? 'Saving...' : 'Add Lead'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Apollo Search Modal ──────────────────────────────────────────────────────
function ApolloModal({ company, onClose, onImport }) {
  const [titles,   setTitles]   = useState('CTO, VP Engineering, IT Director');
  const [industry, setIndustry] = useState('');
  const [results,  setResults]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [importMsg, setImportMsg] = useState('');

  const search = async () => {
    setLoading(true);
    setImportMsg('');
    try {
      const r = await api.post('/leads/apollo/search', { titles: titles.split(',').map(t => t.trim()), industry, company });
      setResults(r.data.leads || []);
    } catch (err) {
      const msg = err.response?.data?.error || '';
      if (msg.toLowerCase().includes('api key') || msg.toLowerCase().includes('apollo')) {
        alert('Apollo API key not set in Settings. Please add APOLLO_API_KEY to your .env file.');
      } else {
        alert(msg || 'Apollo search failed. Check your API key.');
      }
    }
    finally { setLoading(false); }
  };

  const toggle = i => {
    const s = new Set(selected);
    s.has(i) ? s.delete(i) : s.add(i);
    setSelected(s);
  };

  const importSelected = async () => {
    const toImport = results.filter((_, i) => selected.has(i));
    try {
      const r = await api.post('/leads/bulk', { leads: toImport });
      const imported = r.data.imported || toImport.length;
      const dupes = r.data.duplicates || 0;
      setImportMsg(`${imported} leads imported, ${dupes} duplicates skipped`);
      setTimeout(() => { onImport(imported); onClose(); }, 1500);
    } catch (err) { alert('Import failed'); }
  };

  return (
    <Modal title="🎯 Apollo.io Lead Search" onClose={onClose} width={680}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <Input label="Job Titles (comma separated)" value={titles} onChange={e => setTitles(e.target.value)} />
        <Input label="Industry (optional)" value={industry} onChange={e => setIndustry(e.target.value)} placeholder="SaaS, Healthcare..." />
      </div>
      <Btn primary small onClick={search} disabled={loading}>{loading ? 'Searching...' : '🔍 Search Apollo'}</Btn>

      {importMsg && (
        <div style={{ marginTop: 12, background: 'var(--green)11', border: '1px solid var(--green)33', borderRadius: 7, padding: '10px 14px', fontSize: 13, color: 'var(--green)' }}>
          ✅ {importMsg}
        </div>
      )}

      {results.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>{results.length} results — select to import</div>
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {results.map((r, i) => (
              <div key={i} onClick={() => toggle(i)} style={{
                display: 'flex', gap: 12, alignItems: 'center',
                padding: '10px 12px', borderRadius: 7, cursor: 'pointer', marginBottom: 4,
                background: selected.has(i) ? 'var(--accent)11' : 'var(--surface)',
                border: `1px solid ${selected.has(i) ? 'var(--accent)44' : 'var(--border)'}`,
              }}>
                <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${selected.has(i) ? 'var(--accent)' : 'var(--border)'}`, background: selected.has(i) ? 'var(--accent)' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#000' }}>
                  {selected.has(i) && '✓'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
                  <div style={{ color: 'var(--muted)', fontSize: 12 }}>{r.title} · {r.firmName} · {r.industry}</div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{r.email}</div>
              </div>
            ))}
          </div>
          {selected.size > 0 && (
            <div style={{ marginTop: 12 }}>
              <Btn primary onClick={importSelected}>Import {selected.size} Lead{selected.size > 1 ? 's' : ''}</Btn>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── CSV Import Modal ─────────────────────────────────────────────────────────
function CSVImportModal({ company, onClose, onImport }) {
  const [csvText, setCsvText] = useState('');
  const [preview, setPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState('');

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
    return lines.slice(1).map(line => {
      const vals = line.split(',').map(v => v.trim().replace(/"/g, ''));
      const obj = {};
      headers.forEach((h, i) => obj[h] = vals[i] || '');
      return obj;
    }).filter(r => r.name && r.email);
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      setCsvText(text);
      setPreview(parseCSV(text).slice(0, 5));
    };
    reader.readAsText(file);
  };

  const handleTextChange = (text) => {
    setCsvText(text);
    setPreview(parseCSV(text).slice(0, 5));
  };

  const doImport = async () => {
    const parsed = parseCSV(csvText);
    if (!parsed.length) return alert('No valid rows found. Make sure CSV has name and email columns.');
    setImporting(true);
    try {
      const r = await api.post('/leads/bulk', { leads: parsed.map(l => ({ ...l, company })) });
      const imported = r.data.imported || parsed.length;
      const dupes = r.data.duplicates || 0;
      setResult(`${imported} imported, ${dupes} duplicates skipped`);
      setTimeout(() => { onImport(imported); onClose(); }, 1500);
    } catch { alert('Import failed'); }
    finally { setImporting(false); }
  };

  const parsed = parseCSV(csvText);

  return (
    <Modal title="📂 CSV Import" onClose={onClose} width={600}>
      <div style={{ fontSize: 12, color: 'var(--muted)', background: 'var(--surface)', borderRadius: 7, padding: '10px 14px', marginBottom: 16 }}>
        💡 CSV must have columns: <strong>name</strong>, <strong>email</strong>, <strong>type</strong> (bdm/recruiting), <strong>company</strong> (optional: phone, title, industry, skill)
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Upload CSV File</label>
        <input type="file" accept=".csv" onChange={handleFile} style={{ fontSize: 13, color: 'var(--text)' }} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Or Paste CSV Content</label>
        <textarea
          value={csvText}
          onChange={e => handleTextChange(e.target.value)}
          rows={5}
          placeholder={'name,email,type,company\nJohn Smith,john@acme.com,bdm,Acme Corp'}
          style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 12px', color: 'var(--text)', fontSize: 12, resize: 'vertical', fontFamily: 'monospace' }}
        />
      </div>
      {preview.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Preview ({parsed.length} total rows):</div>
          <div style={{ background: 'var(--surface)', borderRadius: 7, overflow: 'hidden', border: '1px solid var(--border)' }}>
            {preview.map((row, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, padding: '8px 12px', borderBottom: '1px solid var(--border)22', fontSize: 12 }}>
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>{row.name}</span>
                <span style={{ color: 'var(--muted)' }}>{row.email}</span>
                <span style={{ color: 'var(--accent)' }}>{row.type || 'bdm'}</span>
                <span style={{ color: 'var(--mid)' }}>{row.company || row.firmName || ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {result && (
        <div style={{ background: 'var(--green)11', border: '1px solid var(--green)33', borderRadius: 7, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: 'var(--green)' }}>
          ✅ {result}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Btn small onClick={onClose}>Cancel</Btn>
        <Btn primary small onClick={doImport} disabled={importing || !parsed.length}>
          {importing ? 'Importing...' : `Import ${parsed.length} Lead${parsed.length !== 1 ? 's' : ''}`}
        </Btn>
      </div>
    </Modal>
  );
}

// ── Enroll in Sequence Modal ─────────────────────────────────────────────────
function EnrollModal({ lead, company, sequences, onClose }) {
  const [seqId, setSeqId] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [done, setDone] = useState(false);

  const filtered = sequences.filter(s => !s.type || s.type === lead.type || s.status === 'active');

  const doEnroll = async () => {
    if (!seqId) return alert('Select a sequence');
    setEnrolling(true);
    try {
      await api.patch(`/sequences/${seqId}`, { addLead: lead._id });
      await api.patch(`/leads/${lead._id}`, { sequenceId: seqId, sequenceStep: 0 });
      setDone(true);
      setTimeout(onClose, 1200);
    } catch (err) { alert(err.response?.data?.error || 'Enrollment failed'); }
    finally { setEnrolling(false); }
  };

  return (
    <Modal title={`🔁 Enroll in Sequence — ${lead.name}`} onClose={onClose}>
      {done ? (
        <div style={{ textAlign: 'center', padding: 20, color: 'var(--green)', fontWeight: 700 }}>✅ Lead enrolled in sequence!</div>
      ) : (
        <>
          {filtered.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 13, padding: 16, textAlign: 'center' }}>No active sequences found. Create one in the Sequences page first.</div>
          ) : (
            <Select label="Select Sequence" value={seqId} onChange={e => setSeqId(e.target.value)}>
              <option value="">— Choose sequence —</option>
              {filtered.map(s => <option key={s._id} value={s._id}>{s.name} ({s.type}) · {s.steps?.length || 0} steps</option>)}
            </Select>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
            <Btn small onClick={onClose}>Cancel</Btn>
            <Btn primary small onClick={doEnroll} disabled={enrolling || !seqId}>{enrolling ? 'Enrolling...' : 'Enroll'}</Btn>
          </div>
        </>
      )}
    </Modal>
  );
}

// ── Main Leads Page ──────────────────────────────────────────────────────────
export default function Leads() {
  const { company, mode } = useOutletContext();
  const [leads,           setLeads]           = useState([]);
  const [total,           setTotal]           = useState(0);
  const [page,            setPage]            = useState(1);
  const [search,          setSearch]          = useState('');
  const [status,          setStatus]          = useState('');
  const [loading,         setLoading]         = useState(false);
  const [emailTarget,     setEmailTarget]     = useState(null);
  const [callTarget,      setCallTarget]      = useState(null);
  const [aiCallTarget,    setAiCallTarget]    = useState(null);
  const [waTarget,        setWaTarget]        = useState(null);
  const [showAdd,         setShowAdd]         = useState(false);
  const [showApollo,      setShowApollo]      = useState(false);
  const [showCSV,         setShowCSV]         = useState(false);

  // Lead Detail Drawer
  const [selectedLead,    setSelectedLead]    = useState(null);
  const [leadActivities,  setLeadActivities]  = useState([]);
  const [loadingDetail,   setLoadingDetail]   = useState(false);

  // Sequence enrollment
  const [sequences,       setSequences]       = useState([]);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollTarget,    setEnrollTarget]    = useState(null);

  // Bulk selection
  const [selectedLeads,   setSelectedLeads]   = useState(new Set());

  // Bulk enroll state
  const [showBulkEnroll,  setShowBulkEnroll]  = useState(false);
  const [bulkSeqId,       setBulkSeqId]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/leads', { params: { company, type: mode, status, search, page, limit: 20 } });
      setLeads(r.data.leads); setTotal(r.data.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [company, mode, status, search, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get(`/sequences?company=${encodeURIComponent(company)}`)
      .then(r => setSequences(r.data || []))
      .catch(() => {});
  }, [company]);

  const openLead = async (lead) => {
    setSelectedLead(lead);
    setLoadingDetail(true);
    try {
      const r = await api.get(`/leads/${lead._id}/activities`);
      setLeadActivities(r.data || []);
    } catch { setLeadActivities([]); }
    setLoadingDetail(false);
  };

  const deleteLead = async id => {
    if (!window.confirm('Delete this lead?')) return;
    await api.delete(`/leads/${id}`);
    setLeads(ls => ls.filter(l => l._id !== id));
    setSelectedLeads(s => { const n = new Set(s); n.delete(id); return n; });
  };

  const enrollInSequence = (lead) => {
    setEnrollTarget(lead);
    setShowEnrollModal(true);
  };

  const createDealFromLead = async (lead) => {
    try {
      await api.post('/pipeline', {
        title: `${lead.name} — ${lead.firmName || 'Deal'}`,
        leadName: lead.name,
        company,
        type: mode,
        stage: 'new',
        value: 0,
      });
      alert('Deal created in Pipeline!');
    } catch { alert('Failed to create deal'); }
  };

  // Bulk actions
  const toggleSelect = (id) => setSelectedLeads(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelectedLeads(new Set(leads.map(l => l._id)));
  const clearAll = () => setSelectedLeads(new Set());

  const bulkEnroll = () => setShowBulkEnroll(true);

  const bulkEmail = () => {
    const targets = leads.filter(l => selectedLeads.has(l._id));
    if (!targets.length) return;
    setEmailTarget(targets[0]);
  };

  const exportSelected = () => {
    const selected = leads.filter(l => selectedLeads.has(l._id));
    const headers = ['name','email','phone','title','firmName','industry','type','status','skill'];
    const rows = selected.map(l => headers.map(h => (l[h] || '').toString().replace(/,/g, ';')).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'leads_export.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const doBulkEnroll = async () => {
    if (!bulkSeqId) return alert('Select a sequence');
    const ids = [...selectedLeads];
    try {
      await Promise.all(ids.map(id => api.patch(`/leads/${id}`, { sequenceId: bulkSeqId, sequenceStep: 0 })));
      await api.patch(`/sequences/${bulkSeqId}`, { addLeads: ids });
      alert(`${ids.length} leads enrolled!`);
      setShowBulkEnroll(false);
      clearAll();
    } catch { alert('Bulk enroll partially failed'); }
  };

  return (
    <div style={{ padding: 28 }}>
      <PageHeader
        title={mode === 'bdm' ? '🎯 BDM Leads' : '👔 Consultant Leads'}
        sub={`${total} leads · ${company}`}
      >
        <Btn small onClick={() => setShowApollo(true)}>🔍 Apollo Search</Btn>
        <Btn small onClick={() => setShowCSV(true)}>📂 Import CSV</Btn>
        <Btn primary small onClick={() => setShowAdd(true)}>+ Add Lead</Btn>
      </PageHeader>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search name, email, company..."
          style={{ flex: 1, minWidth: 200, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 7, padding: '9px 14px', color: 'var(--text)', fontSize: 13, outline: 'none' }}
        />
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 7, padding: '9px 12px', color: 'var(--text)', fontSize: 13, cursor: 'pointer' }}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Bulk actions bar */}
      {selectedLeads.size > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 700 }}>{selectedLeads.size} selected</span>
          <Btn small onClick={bulkEnroll}>🔁 Enroll in Sequence</Btn>
          <Btn small onClick={bulkEmail}>✉️ Send Email</Btn>
          <Btn small onClick={exportSelected}>📥 Export CSV</Btn>
          <Btn small danger onClick={clearAll}>✕ Clear</Btn>
        </div>
      )}

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading...</div>
        ) : leads.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
            <div style={{ color: 'var(--muted)', fontSize: 14 }}>No leads yet. Add manually or search Apollo.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '12px 14px', width: 40 }}>
                    <input type="checkbox" onChange={e => e.target.checked ? selectAll() : clearAll()} checked={selectedLeads.size === leads.length && leads.length > 0} />
                  </th>
                  {['Name', 'Title / Skill', 'Company', 'Email', 'Phone', 'Status', 'Score', 'Actions'].map(h => (
                    <th key={h} style={{ color: 'var(--muted)', fontWeight: 600, padding: '12px 14px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: .5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map(lead => {
                  const score = calcScore(lead);
                  return (
                    <tr
                      key={lead._id}
                      onClick={() => openLead(lead)}
                      style={{ borderBottom: '1px solid var(--border)22', cursor: 'pointer', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '11px 14px' }} onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedLeads.has(lead._id)} onChange={() => toggleSelect(lead._id)} />
                      </td>
                      <td style={{ padding: '11px 14px', fontWeight: 700 }}>{lead.name}</td>
                      <td style={{ padding: '11px 14px', color: 'var(--mid)' }}>{lead.title || lead.skill}</td>
                      <td style={{ padding: '11px 14px', color: 'var(--muted)' }}>{lead.firmName}</td>
                      <td style={{ padding: '11px 14px', color: 'var(--muted)', fontSize: 12 }}>{lead.email}</td>
                      <td style={{ padding: '11px 14px', color: 'var(--muted)', fontSize: 12 }}>{lead.phone || '—'}</td>
                      <td style={{ padding: '11px 14px' }}><Badge label={lead.status} color={STATUS_COLORS[lead.status] || 'var(--muted)'} /></td>
                      <td style={{ padding: '11px 14px', fontSize: 13 }}>{scoreLabel(score)} {score}</td>
                      <td style={{ padding: '11px 14px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Btn small onClick={() => setEmailTarget(lead)}>✉️</Btn>
                          <Btn small onClick={() => setAiCallTarget(lead)} style={{ background: 'var(--accent)22', borderColor: 'var(--accent)44', color: 'var(--accent)' }}>🤖📞</Btn>
                          <Btn small onClick={() => setCallTarget(lead)}>📞</Btn>
                          <Btn small onClick={() => setWaTarget(lead)} style={{ background: '#25D36622', borderColor: '#25D36644', color: '#25D366' }}>💬</Btn>
                          <Btn small onClick={() => enrollInSequence(lead)}>🔁</Btn>
                          <Btn small danger onClick={() => deleteLead(lead._id)}>🗑</Btn>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {total > 20 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <Btn small disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</Btn>
          <span style={{ padding: '6px 14px', color: 'var(--muted)', fontSize: 13 }}>Page {page} of {Math.ceil(total / 20)}</span>
          <Btn small disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>Next →</Btn>
        </div>
      )}

      {/* Lead Detail Drawer */}
      {selectedLead && (
        <div style={{
          position: 'fixed', right: 0, top: 0, bottom: 0, width: 400,
          background: 'var(--card)', borderLeft: '1px solid var(--border)',
          zIndex: 150, overflowY: 'auto', padding: 24,
          boxShadow: '-4px 0 20px rgba(0,0,0,0.3)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ fontWeight: 800, fontSize: 16 }}>{selectedLead.name}</div>
            <button onClick={() => setSelectedLead(null)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 20 }}>✕</button>
          </div>
          {/* Contact info */}
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Contact Info</div>
            {[
              ['Title', selectedLead.title],
              ['Company', selectedLead.firmName],
              ['Email', selectedLead.email],
              ['Phone', selectedLead.phone],
              ['Industry', selectedLead.industry],
              ['Skill', selectedLead.skill],
              ['Source', selectedLead.source],
              ['Status', selectedLead.status],
            ].filter(([, v]) => v).map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
                <span style={{ color: 'var(--muted)' }}>{label}</span>
                <span style={{ color: 'var(--text)', fontWeight: 500 }}>{value}</span>
              </div>
            ))}
            {selectedLead.linkedin && (
              <a href={selectedLead.linkedin} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontSize: 12 }}>→ View LinkedIn</a>
            )}
          </Card>
          {/* Quick actions */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <Btn small primary onClick={() => { setShowEnrollModal(false); setEmailTarget(selectedLead); }}>✉️ Email</Btn>
            <Btn small onClick={() => enrollInSequence(selectedLead)}>🔁 Add to Sequence</Btn>
            <Btn small onClick={() => createDealFromLead(selectedLead)}>📊 Create Deal</Btn>
          </div>
          {/* Notes */}
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Notes</div>
            <div style={{ fontSize: 13, color: 'var(--mid)', whiteSpace: 'pre-wrap' }}>{selectedLead.notes || 'No notes yet.'}</div>
          </Card>
          {/* Activity timeline */}
          <Card>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Activity History</div>
            {loadingDetail ? <div style={{ color: 'var(--muted)', fontSize: 13 }}>Loading...</div> :
             leadActivities.length === 0 ? <div style={{ color: 'var(--muted)', fontSize: 13 }}>No activity yet.</div> :
             leadActivities.map((act, i) => (
               <div key={i} style={{ borderBottom: '1px solid var(--border)', paddingBottom: 10, marginBottom: 10 }}>
                 <div style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700 }}>{act.type?.replace(/_/g, ' ').toUpperCase()}</div>
                 <div style={{ fontSize: 13, color: 'var(--mid)', marginTop: 2 }}>{act.subject}</div>
                 <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{new Date(act.createdAt).toLocaleDateString()}</div>
               </div>
             ))
            }
          </Card>
        </div>
      )}

      {/* Bulk Enroll Modal */}
      {showBulkEnroll && (
        <Modal title={`🔁 Enroll ${selectedLeads.size} Leads in Sequence`} onClose={() => setShowBulkEnroll(false)}>
          <Select label="Select Sequence" value={bulkSeqId} onChange={e => setBulkSeqId(e.target.value)}>
            <option value="">— Choose sequence —</option>
            {sequences.map(s => <option key={s._id} value={s._id}>{s.name} ({s.type})</option>)}
          </Select>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
            <Btn small onClick={() => setShowBulkEnroll(false)}>Cancel</Btn>
            <Btn primary small onClick={doBulkEnroll} disabled={!bulkSeqId}>Enroll All</Btn>
          </div>
        </Modal>
      )}

      {emailTarget   && <EmailModal    lead={emailTarget}  company={company} onClose={() => { setEmailTarget(null);  load(); }} />}
      {callTarget    && <CallModal     lead={callTarget}   company={company} onClose={() => { setCallTarget(null);   load(); }} />}
      {aiCallTarget  && <AICallModal   lead={aiCallTarget} company={company} onClose={() => { setAiCallTarget(null); load(); }} />}
      {waTarget      && <WhatsAppModal lead={waTarget}     company={company} onClose={() => { setWaTarget(null);     load(); }} />}
      {showAdd       && <AddLeadModal  company={company} onClose={() => setShowAdd(false)} onAdded={l => { setLeads(ls => [l, ...ls]); setTotal(t => t + 1); }} />}
      {showApollo    && <ApolloModal   company={company} onClose={() => setShowApollo(false)} onImport={n => { alert(`${n} leads imported!`); load(); }} />}
      {showCSV       && <CSVImportModal company={company} onClose={() => setShowCSV(false)} onImport={n => { load(); }} />}
      {showEnrollModal && enrollTarget && (
        <EnrollModal
          lead={enrollTarget}
          company={company}
          sequences={sequences}
          onClose={() => { setShowEnrollModal(false); setEnrollTarget(null); load(); }}
        />
      )}
    </div>
  );
}
